<template>
  <form novalidate @submit.prevent="save">
    <div class="dhead"><div><h2>Nueva toma</h2><div class="sub">Usado recibido como parte de pago de un 0 km</div></div></div>
    <p class="note">Los campos con <span class="req" aria-hidden="true">*</span> son obligatorios.</p>
    <section class="blk edit nobadge"><h3><span class="n">1</span>Fotos</h3>
      <p class="note">Cargá la foto de la cédula de frente (obligatoria), de cerca y sin reflejos. El dorso es opcional.</p>
      <h4>Documentación</h4>
      <div class="ph"><div v-for="[k, label, req] in documentSlots" :key="k"><TomaPhotos :photos="photos[k] ? [photos[k]] : []" editable replace-on-click source-choices :max="1" :label="label" :required="!!req" :disabled="busy" @add="setPhoto(k, $event)" @replace="setPhoto(k, $event.photo)" @remove="setPhoto(k, null)" @notify="$emit('notify', $event)" /></div></div>
      <p class="note" role="status" aria-live="polite">{{ extractionStatus || (ocr ? 'La lectura comienza al cargar las fotos.' : 'Lectura automática no disponible. Completá los datos a mano.') }}</p>
      <button v-if="ocr" class="btn" type="button" :disabled="busy || extractionPending || !Object.keys(photos).length" @click="retryExtraction">Reintentar lectura</button>
      <h3 class="spaced"><span class="n">2</span>Datos del usado</h3><div class="f">
        <label v-for="[key, label] in vehicleFields" :key="key" class="fl"><span>{{ label }}{{ key === 'dominio' ? '' : ' (opcional)' }}<span v-if="key === 'dominio'" class="req" aria-hidden="true">*</span></span><input v-model="draft.vehiculo[key]" @input="markEdited(key)" :required="key === 'dominio'" :maxlength="150" :type="key === 'anio' ? 'number' : 'text'"></label>
        <label class="fl"><span>Km declarados (opcional)</span><input v-model.number="draft.kmDeclarado" @input="markEdited('kmDeclarado')" type="number" min="0"></label>
      </div>
      <h3 class="spaced"><span class="n">3</span>Operación</h3><div class="f">
        <label class="fl"><span>Vendedor<span class="req" aria-hidden="true">*</span></span><input v-if="sellerLocked" :value="sellerDisplay" disabled><input v-else v-model="vendorName" list="tomaVendedores" maxlength="200" placeholder="Escribí el nombre del vendedor"></label>
        <datalist id="tomaVendedores"><option v-for="v in vendedores" :key="v.id" :value="v.nombre"></option></datalist>
        <label class="fl"><span>Cliente<span class="req" aria-hidden="true">*</span></span><input v-model="draft.cliente" @input="markEdited('cliente')" maxlength="200" required></label>
        <label v-for="[key, label] in contactFields" :key="key" class="fl">{{ label }} (opcional)<input v-model="draft.clienteDatos[key]" @input="markEdited('clienteDatos.' + key)" maxlength="200" :type="key === 'mail' ? 'email' : key === 'telefono' ? 'tel' : 'text'"></label>
        <label class="fl">0 km / n° de operación (opcional)<input v-model="draft.operacion" maxlength="200" placeholder="Ej.: Tracker LTZ · op. 1234"></label>
      </div>
      <p class="note">Completá al menos uno de los dos: teléfono o mail.</p>
      <label class="chk spaced"><input v-model="draft.titularDistinto" type="checkbox">El titular de la cédula no es el cliente</label>
      <div v-if="draft.titularDistinto" class="alert">El titular no coincide con el cliente: para el 08 hace falta la firma del titular registral (o su autorización). Avisale al cliente ahora.</div>
      <label class="fl spaced">Observaciones para el taller (opcional)<textarea v-model="draft.obsVendedor" maxlength="2000" placeholder="Ej.: el cliente avisa que hace ruido el tren delantero"></textarea></label>
      <div class="btns"><button class="btn pri" :disabled="busy" type="submit">{{ busy ? 'Enviando…' : 'Pedir peritaje al taller' }}</button><button v-if="outOfHours" class="btn ghost" type="button" :disabled="busy" @click="openOutOfHours">Pedir peritaje fuera de horario</button></div>
    </section>
    <div v-if="ooOpen" class="oo-backdrop" @click.self="closeOutOfHours" @keydown.esc.stop.prevent="closeOutOfHours">
      <div class="oo-dialog" role="dialog" aria-modal="true" aria-labelledby="oo-title" tabindex="-1">
        <h3 id="oo-title">Pedir peritaje fuera de horario</h3>
        <label class="fl"><span>Patente (obligatorio)</span><input ref="ooPatente" v-model.trim="oo.patente" maxlength="10" placeholder="Ej.: AB123CD" @keydown.enter.prevent="submitOutOfHours"></label>
        <label class="fl"><span>Nombre (obligatorio)</span><input v-model.trim="oo.nombre" maxlength="200" placeholder="Nombre del cliente" @keydown.enter.prevent="submitOutOfHours"></label>
        <div class="fl" role="group" aria-labelledby="oo-dia-label">
          <span id="oo-dia-label">Día (obligatorio)</span>
          <p v-if="ooLoading" class="note">Buscando disponibilidad…</p>
          <div v-else-if="ooDias.length" class="oo-cal">
            <div class="oo-cal-head"><button type="button" class="oo-cal-nav" :disabled="busy || !ooCanPrevMonth" aria-label="Mes anterior" @click="ooShiftMonth(-1)">‹</button><b>{{ ooMonthLabel }}</b><button type="button" class="oo-cal-nav" :disabled="busy || !ooCanNextMonth" aria-label="Mes siguiente" @click="ooShiftMonth(1)">›</button></div>
            <div class="oo-cal-week" aria-hidden="true"><span v-for="name in ooWeekdays" :key="name">{{ name }}</span></div>
            <div class="oo-cal-grid"><template v-for="(cell, index) in ooCalendarDays"><span v-if="!cell" :key="'blank-' + index" class="oo-cal-blank"></span><button v-else :key="cell.fecha" type="button" class="oo-cal-day" :class="{ on: cell.selected }" :disabled="busy || !cell.available" :aria-pressed="cell.selected ? 'true' : 'false'" @click="ooPickDay(cell.fecha)">{{ cell.day }}</button></template></div>
          </div>
        </div>
        <label class="fl"><span>Hora (obligatorio)</span><select v-model="oo.hora" :disabled="busy || !ooDay"><option value="" disabled>{{ ooDay ? 'Elegí una hora' : 'Elegí un día' }}</option><option v-for="h in ooHoras" :key="h" :value="h">{{ h }}</option></select></label>
        <p v-if="ooError" class="note" role="alert">{{ ooError }}</p>
        <div class="btns"><button class="btn ghost" type="button" :disabled="busy" @click="closeOutOfHours">Cancelar</button><button class="btn pri" type="button" :disabled="busy" @click="submitOutOfHours">Confirmar</button></div>
      </div>
    </div>
  </form>
</template>
<script>
import TomaPhotos from './TomaPhotos.vue'
import { number, normalize, createOcrImage } from '../../../util/tomaUsados'
// Seller lens: the artifact locked the seller input to the signed-in person.
const selfOf = (vendedores, selfUserId) => selfUserId == null ? null : (vendedores || []).find(v => Number(v.uid) === Number(selfUserId)) || null
// Only local, allowlisted copy is shown; never display an upstream error message.
const extractionMessages = {
  EXTRACTION_CONFIG_MISSING: 'La lectura no está configurada. Solicitá a Sistemas que configure la clave de OpenRouter en el servidor.',
  EXTRACTION_UPSTREAM_REQUEST: 'OpenRouter rechazó los parámetros de lectura. Solicitá a Sistemas que revise la compatibilidad de la solicitud.',
  EXTRACTION_UPSTREAM_AUTH: 'OpenRouter rechazó la clave del servidor. Solicitá a Sistemas que revise la credencial; no hace falta cerrar tu sesión.',
  EXTRACTION_UPSTREAM_CREDITS: 'OpenRouter informó saldo insuficiente. Solicitá al administrador que revise el saldo de la cuenta.',
  EXTRACTION_UPSTREAM_FORBIDDEN: 'OpenRouter denegó el acceso. Solicitá al administrador que revise permisos, restricciones de la clave y límites de gasto.',
  EXTRACTION_UPSTREAM_NO_PROVIDER: 'OpenRouter no encontró una ruta para el modelo con la política de privacidad exigida. Solicitá a Sistemas que revise la disponibilidad del proveedor sin reducir la privacidad.',
  EXTRACTION_UPSTREAM_RATE_LIMIT: 'OpenRouter alcanzó un límite de solicitudes. Esperá unos minutos antes de reintentar.',
  EXTRACTION_UPSTREAM_FAILURE: 'El proveedor de lectura falló. Reintentá más tarde; si persiste, avisá a Sistemas.',
  EXTRACTION_TIMEOUT: 'La lectura superó el tiempo de espera. Reintentá más tarde; si persiste, avisá a Sistemas.',
  EXTRACTION_NETWORK: 'Falló la conexión del servidor con OpenRouter. Reintentá más tarde o avisá a Sistemas.',
  EXTRACTION_RESPONSE_TOO_LARGE: 'La respuesta de lectura es demasiado grande. Avisá a Sistemas para revisar el servicio.',
  EXTRACTION_INVALID_RESPONSE: 'El proveedor devolvió una respuesta inválida. Reintentá más tarde; si persiste, avisá a Sistemas.',
  EXTRACTION_TRUNCATED: 'El proveedor devolvió una lectura incompleta. Reintentá con una foto más clara; si persiste, avisá a Sistemas.',
  EXTRACTION_INVALID_RESULT: 'La lectura no produjo datos válidos para estas fotos. Revisá que se vean la cédula y el odómetro total en km.',
  EXTRACTION_BUSY: 'Hay demasiadas lecturas simultáneas. Esperá un momento antes de reintentar.',
  EXTRACTION_RATE_LIMIT: 'Alcanzaste el límite de intentos de lectura. Esperá cinco minutos antes de reintentar.',
  PERMISSION_DENIED: 'No tenés permiso para solicitar lecturas. Consultá al administrador.',
  COMPANY_DENIED: 'La empresa no está habilitada para esta lectura. Volvé a seleccionar una empresa autorizada.',
  INVALID_EXTRACTION_PHOTOS: 'Seleccioná únicamente frente, dorso, DNI frente, DNI dorso y tablero, sin repetir fotos.',
  INVALID_IMAGE: 'La imagen no es válida. Volvé a cargar una foto JPG, PNG o WebP.',
  OCR_IMAGE_PREPARATION: 'No se pudo preparar una copia de las fotos para lectura. Intenta con una imagen más clara.',
  PHOTO_TOO_LARGE: 'La foto supera el tamaño permitido. Cargá una imagen más pequeña.',
  BODY_TOO_LARGE: 'Las fotos superan el tamaño permitido. Cargá imágenes más pequeñas.'
}
export default {
  name: 'TomaNew', components: { TomaPhotos }, emits: ['save', 'notify', 'save-out-of-hours'], props: { empresaId: Number, vendedores: Array, busy: Boolean, ocr: Boolean, extract: Function, availability: Function, sessionContext: { type: String, default: '' }, sucursal: { type: Number, default: null }, selfUserId: { type: Number, default: null }, selfName: { type: String, default: '' }, outOfHours: Boolean },
  data () {
    const vehicleFields = [['dominio', 'Dominio (sale de la cédula)'], ['marca', 'Marca'], ['modelo', 'Modelo'], ['tipo', 'Versión / tipo'], ['anio', 'Año'], ['motor', 'N° de motor'], ['chasis', 'N° de chasis'], ['titular', 'Titular (según cédula)'], ['documento', 'DNI / CUIT del titular'], ['color', 'Color']]
    const selfVendor = selfOf(this.vendedores, this.selfUserId)
    // Typed vendor name; suggestions come from the active master, free text is allowed.
    const vendorName = this.selfUserId != null
      ? (selfVendor ? selfVendor.nombre : (this.selfName || ''))
      : this.vendedores.length === 1 ? this.vendedores[0].nombre : ''
    return { extractionGeneration: 0, extractionTimer: null, extractionPending: false, extractionStatus: '', editedFields: {}, automaticFields: {}, extractionDestroyed: false, ooOpen: false, oo: { patente: '', nombre: '', dia: '', hora: '' }, ooDias: [], ooMonthCursor: null, ooWeekdays: ['Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sá', 'Do'], ooLoading: false, ooError: '', ooGeneration: 0,
      vehicleFields, contactFields: [['telefono', 'Teléfono'], ['mail', 'Mail'], ['direccion', 'Dirección'], ['localidad', 'Localidad']], slots: [['frente', 'Cédula verde · frente', true], ['dorso', 'Cédula verde · dorso', false]], photos: {}, vendorName,
      draft: { empresa_id: this.empresaId, sucursal: this.sucursal, cliente: '', clienteDatos: { telefono: '', mail: '', direccion: '', localidad: '' }, operacion: '', vehiculo: Object.fromEntries(vehicleFields.map(([k]) => [k, ''])), kmDeclarado: null, titularDistinto: false, obsVendedor: '' } }
  },
  computed: {
    selfVendor () { return selfOf(this.vendedores, this.selfUserId) },
    sellerLocked () { return this.selfUserId != null },
    sellerDisplay () { return this.selfVendor ? this.selfVendor.nombre : (this.selfName || '') },
    documentSlots () { return this.slots.map(([key, , required]) => [key, `Foto de cédula (${key})${required ? '' : ' (opcional)'}`, required]) },
    ooDay () { return this.ooDias.find(day => day.fecha === this.oo.dia) || null },
    ooHoras () {
      if (!this.ooDay) return []
      const now = Date.now() - 5 * 60000
      return this.ooDay.horas.filter(time => this.ooSlotEpoch(time) > now)
    },
    ooMonthKeys () { return [...new Set(this.ooDias.map(day => String(day.fecha).slice(0, 7)))].sort() },
    ooMonth () {
      if (!this.ooMonthKeys.length) return ''
      const cursor = this.ooMonthCursor || this.ooMonthKeys[0]
      if (cursor < this.ooMonthKeys[0]) return this.ooMonthKeys[0]
      if (cursor > this.ooMonthKeys[this.ooMonthKeys.length - 1]) return this.ooMonthKeys[this.ooMonthKeys.length - 1]
      return cursor
    },
    ooMonthLabel () {
      if (!this.ooMonth) return ''
      const [year, month] = this.ooMonth.split('-').map(Number)
      const label = new Intl.DateTimeFormat('es-AR', { month: 'long', year: 'numeric' }).format(new Date(year, month - 1, 1))
      return label.charAt(0).toUpperCase() + label.slice(1)
    },
    ooCanPrevMonth () { return this.ooMonthKeys.indexOf(this.ooMonth) > 0 },
    ooCanNextMonth () { return this.ooMonthKeys.indexOf(this.ooMonth) >= 0 && this.ooMonthKeys.indexOf(this.ooMonth) < this.ooMonthKeys.length - 1 },
    ooCalendarDays () {
      if (!this.ooMonth) return []
      const [year, month] = this.ooMonth.split('-').map(Number)
      const offset = (new Date(year, month - 1, 1).getDay() + 6) % 7
      const total = new Date(year, month, 0).getDate()
      const available = {}
      this.ooDias.forEach(day => { available[day.fecha] = true })
      const cells = Array.from({ length: offset }, () => null)
      for (let day = 1; day <= total; day++) {
        const fecha = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
        cells.push({ fecha, day, available: Boolean(available[fecha]), selected: this.oo.dia === fecha })
      }
      return cells
    }
  },
  watch: {
    'oo.dia' () { this.oo.hora = '' },
    empresaId () { this.invalidateExtraction() },
    sessionContext () { this.invalidateExtraction() },
    ocr () { this.invalidateExtraction() }
  },
  beforeDestroy () { this.extractionDestroyed = true; this.invalidateExtraction(); this.ooGeneration++ },
  methods: {
    markEdited (key) { this.$set(this.editedFields, key, true) },
    extractionTarget (key) {
      if (key === 'cliente' || key === 'kmDeclarado') return [this.draft, key]
      if (key === 'clienteDatos.direccion') return [this.draft.clienteDatos, 'direccion']
      if (key === 'clienteDatos.localidad') return [this.draft.clienteDatos, 'localidad']
      return [this.draft.vehiculo, key]
    },
    invalidateExtraction () {
      this.extractionGeneration++
      clearTimeout(this.extractionTimer)
      this.extractionTimer = null
      this.extractionPending = false
      this.extractionStatus = ''
    },
    setPhoto (slot, photo) {
      this.invalidateExtraction()
      const definition = this.slots.find(([key]) => key === slot)
      if (photo) this.$set(this.photos, slot, definition ? { ...photo, tipo: definition[1] } : photo)
      else this.$delete(this.photos, slot)
      // Only discard suggestions from the changed source, never manually edited fields.
      let keys
      if (slot === 'tablero') keys = ['kmDeclarado']
      else if (slot === 'dni_frente' || slot === 'dni_dorso') keys = ['cliente', 'clienteDatos.direccion', 'clienteDatos.localidad']
      else {
        keys = [...this.vehicleFields.map(([key]) => key), 'cliente', 'clienteDatos.direccion']
        if (['dni_frente', 'dni_dorso'].some(key => key !== slot && this.photos[key])) keys = keys.filter(key => !['cliente', 'clienteDatos.direccion'].includes(key))
      }
      for (const key of keys) if (this.automaticFields[key] && !this.editedFields[key]) {
        const [target, field] = this.extractionTarget(key)
        target[field] = key === 'kmDeclarado' ? null : ''
        this.$set(this.automaticFields, key, false)
      }
      if (this.ocr && !this.extractionDestroyed) {
        const session = this.sessionContext, generation = this.extractionGeneration
        this.extractionStatus = 'Lectura pendiente…'
        this.extractionTimer = setTimeout(() => {
          if (session === this.sessionContext && generation === this.extractionGeneration) return this.runExtraction()
        }, 700)
      }
    },
    retryExtraction () { this.invalidateExtraction(); return this.runExtraction() },
    async runExtraction () {
      clearTimeout(this.extractionTimer)
      this.extractionTimer = null
      if (!this.ocr || !this.extract || this.extractionDestroyed || this.busy) return
      const photos = this.slots.filter(([key]) => this.photos[key]).map(([key]) => ({ tipo: key, data: this.photos[key].data }))
      if (!photos.length) return
      const generation = ++this.extractionGeneration, company = this.empresaId, session = this.sessionContext
      const current = () => !this.extractionDestroyed && this.ocr && generation === this.extractionGeneration && company === this.empresaId && session === this.sessionContext
      this.extractionPending = true
      this.extractionStatus = 'Leyendo fotos… Podés seguir completando los datos.'
      try {
        // Keep storage photos unchanged; OCR receives only the two registration slots.
        const copies = photos.map(photo => createOcrImage(photo.data))
        const images = copies.some(copy => typeof copy !== 'string') ? await Promise.all(copies) : copies
        if (!current()) return
        const data = await this.extract({ empresa_id: company, fotos: photos.map((photo, index) => ({ tipo: photo.tipo, data: images[index] })) }, session)
        if (!current()) return
        const values = { ...Object.fromEntries(this.vehicleFields.map(([key]) => [key, data.vehiculo && data.vehiculo[key]])), kmDeclarado: data.kmDeclarado }
        const hasDni = photos.some(p => p.tipo === 'dni_frente' || p.tipo === 'dni_dorso')
        const hasDoc = photos.some(p => p.tipo === 'frente' || p.tipo === 'dorso')
        values.cliente = hasDni ? (data.cliente || null) : (hasDoc ? (data.vehiculo && data.vehiculo.titular) : null)
        if (hasDoc || hasDni) values['clienteDatos.direccion'] = data.clienteDatos && data.clienteDatos.direccion
        if (hasDni) values['clienteDatos.localidad'] = data.clienteDatos && data.clienteDatos.localidad
        for (const [key, value] of Object.entries(values)) {
          if (!this.vehicleFields.some(([k]) => k === key) && !['kmDeclarado', 'cliente', 'clienteDatos.direccion', 'clienteDatos.localidad'].includes(key)) continue
          const [target, field] = this.extractionTarget(key), previous = target[field]
          if (value == null || (typeof value === 'string' && !value.trim()) || this.editedFields[key] || (!this.automaticFields[key] && previous !== '' && previous != null)) continue
          target[field] = value
          this.$set(this.automaticFields, key, true)
        }
        this.extractionStatus = 'Lectura finalizada. Revisá las sugerencias y completá los datos faltantes.'
      } catch (error) {
        if (current()) {
          const message = Object.prototype.hasOwnProperty.call(extractionMessages, error && error.code) ? extractionMessages[error.code] : 'No se pudo leer las fotos. Reintentá más tarde; si persiste, avisá a Sistemas.'
          this.extractionStatus = message + ' Podés completar y guardar los datos a mano.'
        }
      } finally { if (current()) this.extractionPending = false }
    },
    missingRequired () {
      const missing = []
      if (!this.photos.frente) missing.push('la foto de la cédula verde (frente)')
      if (!normalize(String(this.draft.vehiculo.dominio || '')).replace(/[^A-Z0-9]/g, '')) missing.push('el dominio')
      if (!String(this.vendorName || '').trim()) missing.push('el vendedor')
      if (!String(this.draft.cliente || '').trim()) missing.push('el cliente')
      if (!String(this.draft.clienteDatos.telefono || '').trim() && !String(this.draft.clienteDatos.mail || '').trim()) missing.push('un teléfono o mail')
      return missing
    },
    vendorSelection () {
      const name = String(this.vendorName || '').trim()
      if (!name) return null
      const match = this.sellerLocked ? null : this.vendedores.find(v => normalize(String(v.nombre).trim()) === normalize(name))
      // Exactly one vendor key: master id when the typed name matches, otherwise free text.
      return this.sellerLocked
        ? (this.selfVendor ? { vendedor_id: this.selfVendor.id } : { vendedor_nombre: name })
        : (match ? { vendedor_id: match.id } : { vendedor_nombre: name })
    },
    ooSlotEpoch (time, day = this.oo.dia) {
      if (!day || !time) return NaN
      // The workshop schedule is fixed at UTC-3 regardless of the browser timezone.
      return Date.parse(`${day}T${time}:00-03:00`)
    },
    ooShiftMonth (delta) {
      if (!this.ooMonth || this.busy) return
      const [year, month] = this.ooMonth.split('-').map(Number)
      const target = new Date(year, month - 1 + delta, 1)
      this.ooMonthCursor = `${target.getFullYear()}-${String(target.getMonth() + 1).padStart(2, '0')}`
    },
    ooPickDay (fecha) {
      if (this.busy || !this.ooDias.some(day => day.fecha === fecha)) return
      this.oo.dia = fecha
    },
    async loadAvailability () {
      if (!this.availability) return
      const generation = ++this.ooGeneration
      this.ooDias = []
      this.ooMonthCursor = null
      this.ooError = ''
      this.ooLoading = true
      try {
        const data = await this.availability({ empresa_id: this.empresaId, sucursal: this.sucursal })
        if (generation !== this.ooGeneration) return
        const now = Date.now() - 5 * 60000
        const days = Array.isArray(data && data.dias)
          ? data.dias.slice().sort((a, b) => String(a.fecha).localeCompare(String(b.fecha)))
          : []
        this.ooDias = days.filter(day => Array.isArray(day.horas) && day.horas.some(time => this.ooSlotEpoch(time, day.fecha) > now))
        if (!this.ooDias.length) this.ooError = 'No hay peritos disponibles en los próximos 30 días.'
      } catch (_) {
        if (generation === this.ooGeneration) this.ooError = 'No se pudo consultar la disponibilidad del taller. Reintentá más tarde.'
      } finally {
        if (generation === this.ooGeneration) this.ooLoading = false
      }
    },
    openOutOfHours () {
      if (this.busy) return
      this.oo = { patente: '', nombre: '', dia: '', hora: '' }
      this.ooMonthCursor = null
      this.ooOpen = true
      this.loadAvailability()
      this.$nextTick(() => { if (this.$refs.ooPatente) this.$refs.ooPatente.focus() })
    },
    closeOutOfHours () {
      if (this.busy) return
      this.ooGeneration++
      this.ooOpen = false
    },
    submitOutOfHours () {
      if (this.busy) return
      const patente = normalize(String(this.oo.patente || '')).replace(/[^A-Z0-9]/g, '')
      const nombre = String(this.oo.nombre || '').trim()
      const fecha = this.ooSlotEpoch(this.oo.hora)
      const missing = []
      if (!patente) missing.push('la patente')
      if (!nombre) missing.push('el nombre')
      if (!this.oo.dia) missing.push('el día')
      if (!this.oo.hora) missing.push('la hora')
      if (missing.length) {
        const list = missing.length === 1 ? missing[0] : missing.slice(0, -1).join(', ') + ' y ' + missing[missing.length - 1]
        this.$emit('notify', { message: `Completá los campos: ${list}.`, type: 'error' })
        return
      }
      if (!Number.isFinite(fecha) || fecha < Date.now() - 5 * 60000) {
        this.$emit('notify', { message: 'Elegí un día y una hora disponibles del taller.', type: 'error' })
        return
      }
      const vendor = this.vendorSelection()
      if (!vendor) {
        this.$emit('notify', { message: 'No se pudo identificar al vendedor.', type: 'error' })
        return
      }
      this.$emit('save-out-of-hours', { empresa_id: this.empresaId, sucursal: this.sucursal, patente, nombre, fecha, ...vendor })
    },
    save () {
      if (this.busy) return
      const missing = this.missingRequired()
      if (missing.length) {
        const list = missing.length === 1 ? missing[0] : missing.slice(0, -1).join(', ') + ' y ' + missing[missing.length - 1]
        this.$emit('notify', { message: `Completá los campos obligatorios: ${list}.`, type: 'error' })
        return
      }
      const vendor = this.vendorSelection()
      if (!vendor) {
        this.$emit('notify', { message: 'Escribí el nombre del vendedor.', type: 'error' })
        return
      }
      const d = JSON.parse(JSON.stringify(this.draft))
      d.vehiculo.dominio = normalize(d.vehiculo.dominio).replace(/[^A-Z0-9]/g, '')
      d.vehiculo.anio = number(d.vehiculo.anio); d.kmDeclarado = number(d.kmDeclarado)
      this.$emit('save', { ...d, ...vendor, fotos: this.slots.filter(([key]) => this.photos[key]).map(([key, tipo]) => ({ ...this.photos[key], tipo })) })
    }
  }
}
</script>
<style scoped>
.oo-dialog .btns { justify-content: flex-end; }
.oo-cal { border: 1px solid var(--line); border-radius: 8px; padding: 8px; background: #fff; }
.oo-cal-head { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-bottom: 6px; }
.oo-cal-head b { font-size: .95rem; color: var(--ink); text-transform: capitalize; }
.oo-cal-nav { border: 1px solid var(--line); background: var(--bg); color: var(--ink); border-radius: 6px; width: 28px; height: 28px; line-height: 1; font-size: 1rem; cursor: pointer; }
.oo-cal-nav:disabled { opacity: .45; cursor: not-allowed; }
.oo-cal-week, .oo-cal-grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 2px; }
.oo-cal-week span { text-align: center; font-size: .7rem; font-weight: 700; color: var(--muted); padding: 2px 0; }
.oo-cal-day { border: 0; background: none; border-radius: 6px; height: 30px; font: inherit; font-size: .85rem; color: var(--ink); cursor: pointer; }
.oo-cal-day:disabled { color: var(--muted); opacity: .45; cursor: not-allowed; }
.oo-cal-day:not(:disabled):hover { background: color-mix(in srgb, var(--teal) 14%, transparent); }
.oo-cal-day.on { background: var(--accent); color: var(--accent-ink); font-weight: 700; }
</style>
